import { QuestionRulesConditions } from '@app/src/components/Drawer/Views/DrawerViewAddQuestionRule'
import { useResponseItemContext } from '@app/src/context/ResponseItemContext'
import { ResponseItemView } from '@app/src/context/ResponseItemContextProvider'
import useQuestionRules from '@app/src/hooks/useQuestionRules'
import type { RequestItem } from '@app/src/types/resourceExplorer'
import { AnswerStatus } from '@app/src/types/resourceExplorer'
import { br } from '@app/src/utils/translationMarkup'
import { InfoOutlined } from '@mui/icons-material'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useConfirm } from 'material-ui-confirm'
import type { FC } from 'react'
import { default as React, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import SkipQuestionOptions from './SkipQuestionOptions'

interface SkipQuestionButtonProps {
  requestItem: RequestItem
}

const SkipQuestionButton: FC<SkipQuestionButtonProps> = ({ requestItem }) => {
  const { errors, watch, clearErrors, setValue } = useFormContext()
  const { formatMessage } = useIntl()
  const { responseItemView, setResponseItemView } = useResponseItemContext()
  const confirm = useConfirm()
  const { rulesTriggeredByCurrentQuestion, applySkipRuleEffectsToQuestion } = useQuestionRules(requestItem)

  const requestItemId = requestItem.id
  const cannotAnswer = `${requestItemId}.cannotAnswer`
  const answerStatus = `${requestItemId}.answerStatus`
  const answerStatusComment = `${requestItemId}.comment`

  const hasError = Boolean(errors?.[`${requestItem.id}`])
  const cannotAnswerWatch = watch(cannotAnswer)
  const isRequired = requestItem.isRequired

  useEffect(() => {
    if (!rulesTriggeredByCurrentQuestion?.length) return

    const ruleValue = cannotAnswerWatch ? [QuestionRulesConditions.Skipped] : []

    rulesTriggeredByCurrentQuestion.forEach(rule => {
      if (rule.sectionQuestionTargets?.length > 0) {
        applySkipRuleEffectsToQuestion(requestItem.questionId, ruleValue)
      }
    })
  }, [cannotAnswerWatch, rulesTriggeredByCurrentQuestion, requestItem.questionId, applySkipRuleEffectsToQuestion])

  const onCancel = (): void => {
    if (hasError) clearErrors(`${requestItem.id}`)
    setResponseItemView(ResponseItemView.ShowSuggestedAnswer)
    setValue(cannotAnswer, false, { shouldDirty: true })
    setValue(answerStatus, AnswerStatus.Answered, { shouldDirty: true })
    setValue(answerStatusComment, '')
  }

  if (isRequired) return null

  if (responseItemView !== ResponseItemView.SkipQuestion && !cannotAnswerWatch) {
    return (
      <Box px={4}>
        <Button
          onClick={() => {
            setResponseItemView(ResponseItemView.SkipQuestion)
            setValue(cannotAnswer, true, { shouldDirty: true })
            setValue(answerStatus, AnswerStatus.NotApplicable, { shouldDirty: true })
            if (hasError) clearErrors(`${requestItem.id}`)

            if (rulesTriggeredByCurrentQuestion?.length) {
              setTimeout(() => {
                applySkipRuleEffectsToQuestion(requestItem.questionId, [QuestionRulesConditions.Skipped])
              }, 0)
            }
          }}
          data-testid="button-skip-this-question"
        >
          {formatMessage({ id: 'reporting.skipQuestion.skipThisQuestion' })}
        </Button>
      </Box>
    )
  }

  return (
    <Stack spacing={2}>
      <Stack spacing={2} px={4} py={2} bgcolor="grey.100">
        <Stack direction="row" justifyContent="space-between">
          <Stack>
            <Typography variant="subtitle1">
              {formatMessage({ id: 'reporting.skipQuestion.skipThisQuestion' })}
            </Typography>
            <Typography color="textSecondary">
              {formatMessage({ id: 'reporting.skipQuestion.selectOption' })}
            </Typography>
          </Stack>
          <Button
            size="small"
            onClick={() =>
              confirm({
                title: formatMessage({ id: 'reporting.skipQuestion.aboutTitle' }),
                content: formatMessage({ id: 'reporting.skipQuestion.aboutDescription' }, { br }),
                confirmationText: formatMessage({ id: 'general.close' }),
                confirmationButtonProps: { variant: 'text' },
                hideCancelButton: true,
              })
            }
            sx={{ marginLeft: 0 }}
            startIcon={<InfoOutlined />}
          >
            {formatMessage({ id: 'general.learnMore' })}
          </Button>
        </Stack>
        <SkipQuestionOptions
          requestItemId={requestItem.id}
          cannotAnswer={cannotAnswer}
          answerStatus={answerStatus}
          answerStatusComment={answerStatusComment}
          hasError={hasError}
        />
        <Stack direction="row" spacing={1}>
          <Button variant="text" onClick={onCancel} data-testid="cancel-skip-question">
            {formatMessage({ id: 'reporting.skipQuestion.undoSkip' })}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  )
}

export default SkipQuestionButton
